import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Nav } from "../components/Nav";
import { Footer } from "../components/Footer";

const Section = ({ n, title, children }) => (
  <section className="mb-7" data-testid={`terms-section-${n}`}>
    <h2 className="font-display text-base uppercase tracking-tight text-white sm:text-lg">
      {n}. {title}
    </h2>
    <div className="mt-2 space-y-2 text-[12px] leading-relaxed text-white/65 sm:text-[13px]">
      {children}
    </div>
  </section>
);

const Bullets = ({ items }) => (
  <ul className="ml-4 list-disc space-y-1 marker:text-loudar-orange">
    {items.map((t, i) => (
      <li key={i}>{t}</li>
    ))}
  </ul>
);

const TermsPage = () => {
  return (
    <main
      data-testid="terms-page"
      className="relative min-h-screen bg-loudar-ink text-white"
    >
      <Nav />

      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28">
        <div className="container-loudar max-w-4xl">
          <Link
            to="/"
            className="mb-10 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-white/55 transition-colors hover:text-loudar-orange"
            data-testid="terms-back-link"
          >
            <ArrowLeft size={14} /> Back to Loudar
          </Link>

          <span className="label-mono">Legal</span>
          <h1
            data-testid="terms-headline"
            className="mt-4 font-display text-4xl uppercase leading-[0.95] tracking-tight text-white sm:text-5xl md:text-6xl"
          >
            Terms &amp; Conditions
          </h1>

          <div className="mt-6 h-px w-24 bg-loudar-orange" />

          <div className="mt-10 space-y-2 text-[12px] leading-relaxed text-white/65 sm:text-[13px]">
            <p>
              <span className="text-white/85">Effective Date:</span> 1 May, 2026
            </p>
            <p>
              Welcome to Loudar. These Terms &amp; Conditions govern your use of the
              Loudar platform ("App", "Platform", "Service"). By using Loudar, you agree
              to these terms. If not, you should not use the platform.
            </p>
          </div>

          <div className="mt-12">
            <Section n={1} title="About Loudar">
              <p>
                Loudar is a professional networking platform designed for individuals
                working in or connected to the music industry. It enables users to create
                profiles, share experience, receive reviews, and connect with other
                professionals.
              </p>
            </Section>

            <Section n={2} title="Eligibility">
              <p>To use Loudar:</p>
              <Bullets
                items={[
                  "You must be at least 16 years old (or the minimum legal age in your country)",
                  "You must register using a valid email address",
                  "You must provide accurate and truthful information",
                ]}
              />
              <p>You are responsible for maintaining the confidentiality of your account.</p>
            </Section>

            <Section n={3} title="User Accounts">
              <p>When creating an account:</p>
              <Bullets
                items={[
                  "You agree to provide accurate and up-to-date information",
                  "You are responsible for all activity under your account",
                  "You must not impersonate another person or entity",
                ]}
              />
              <p>
                We reserve the right to suspend or terminate accounts that violate these
                terms.
              </p>
            </Section>

            <Section n={4} title="User Content">
              <p>You retain ownership of any content you upload, including:</p>
              <Bullets items={["Profile information", "Reviews", "Media (images, audio, etc.)"]} />
              <p>
                However, by posting content on Loudar, you grant us a non-exclusive,
                worldwide, royalty-free license to use, display, and distribute your content
                within the platform.
              </p>
              <p>You agree not to post:</p>
              <Bullets
                items={[
                  "False or misleading information",
                  "Defamatory or harmful content",
                  "Content that violates intellectual property rights",
                  "Offensive, abusive, or illegal material",
                ]}
              />
            </Section>

            <Section n={5} title="Definition of Objectionable Content">
              <p>
                For the purposes of this agreement, "Objectionable Content" includes, but is
                not limited to, any material that is:
              </p>
              <Bullets
                items={[
                  "Defamatory or Libelous: Content that harms the reputation of others.",
                  "Discriminatory: Content that promotes violence, hatred, or discrimination based on race, religion, sexual orientation, gender, age, or disability.",
                  "Obscene or Explicit: Pornography, depictions of sexual violence, or overly graphic imagery.",
                  "Threatening or Harassing: Stalking, bullying, or any content intended to intimidate or incite harm against an individual or group.",
                ]}
              />
            </Section>

            <Section n={6} title="No-Tolerance Policy for Abusive Conduct">
              <p>
                Loudar maintains a strict Zero-Tolerance Policy regarding Objectionable
                Content and abusive behavior. We are committed to maintaining a safe,
                respectful environment. Any user found to be engaging in harassment or
                distributing prohibited material will be subject to immediate action as
                outlined in these terms.
              </p>
            </Section>

            <Section n={7} title="User Responsibilities">
              <p>
                You are solely responsible for any content you upload, post, or transmit
                through the service. You represent and warrant that your content does not
                violate any local, state, or international laws and does not contain harmful,
                unlawful, or unauthorized material.
              </p>
            </Section>

            <Section n={8} title="Reporting & Blocking Mechanisms">
              <p>To empower our community, the app provides built-in tools to manage your experience:</p>
              <Bullets
                items={[
                  'Reporting: Users can flag any content or profile that violates these terms via the "Report" button located on the content and user profile.',
                  "Blocking: Users have the right to instantly block any other user to prevent further interaction or visibility of their content.",
                ]}
              />
            </Section>

            <Section n={9} title="24-Hour Review and Action Requirement">
              <p>
                Upon receipt of a user report, Loudar will initiate a review of the flagged
                content or user. We commit to acting on all valid reports within twenty-four
                (24) hours. Actions may include the permanent removal of the content and/or
                the suspension of the offending user's account.
              </p>
            </Section>

            <Section n={10} title="Right to Moderate and Remove">
              <p>
                We reserve the right, but do not assume the obligation, to monitor and review
                all user-generated content. Loudar reserves the sole discretion to remove,
                edit, or delete any content at any time, for any reason, without prior notice
                to the user.
              </p>
            </Section>

            <Section n={11} title="User Suspension and Deactivation">
              <p>
                Violations of this No-Tolerance Policy will result in immediate consequences.
                Loudar reserves the right to ban, eject, or permanently deactivate any account
                found to be in violation of these terms. Users who are banned are prohibited
                from creating new accounts and may be reported to law enforcement if the
                content involves illegal activities.
              </p>
            </Section>

            <Section n={12} title="Reviews & Ratings">
              <p>
                Loudar enables users to leave reviews and ratings based on real collaborations
                and experiences. By using this feature:
              </p>
              <Bullets
                items={[
                  "You agree reviews must be honest and based on real interactions",
                  "You must not manipulate ratings or post fake reviews",
                  "Loudar does not guarantee the accuracy of user-generated reviews",
                ]}
              />
              <p>We reserve the right to remove content that violates our policies.</p>
            </Section>

            <Section n={13} title="Professional Representation Disclaimer">
              <p>Loudar organizes professional reputation data, but:</p>
              <Bullets
                items={[
                  "We do not verify every claim or collaboration",
                  "We do not guarantee job opportunities or outcomes",
                  "We are not responsible for decisions made based on platform content",
                ]}
              />
              <p>Use the platform as a tool, not a guarantee.</p>
            </Section>

            <Section n={14} title="Connections & Interactions">
              <p>Users interact at their own discretion. Loudar is not responsible for:</p>
              <Bullets
                items={[
                  "Disputes between users",
                  "Agreements or collaborations made off-platform",
                  "Conduct of users in real-world interactions",
                ]}
              />
            </Section>

            <Section n={15} title="Account Deletion">
              <p>You may delete your account at any time via the app.</p>
              <p>Upon deletion:</p>
              <Bullets
                items={[
                  "Your profile and personal data will be removed (subject to legal retention requirements)",
                  "Some anonymized data (e.g., reviews without identity) may be retained",
                ]}
              />
            </Section>

            <Section n={16} title="Intellectual Property">
              <p>All platform elements (branding, design, software) are owned by Loudar.</p>
              <p>You may not:</p>
              <Bullets
                items={[
                  "Copy or reproduce the platform",
                  "Reverse engineer or exploit the system",
                  "Use Loudar branding without permission",
                ]}
              />
            </Section>

            <Section n={17} title="Termination">
              <p>We may suspend or terminate your account if:</p>
              <Bullets
                items={[
                  "You violate these Terms",
                  "You misuse the platform",
                  "Required by law",
                ]}
              />
            </Section>

            <Section n={18} title="Limitation of Liability">
              <p>Loudar is provided "as is".</p>
              <p>We are not liable for:</p>
              <Bullets
                items={[
                  "Loss of opportunities or income",
                  "Inaccurate user-generated content",
                  "Platform downtime or errors",
                ]}
              />
            </Section>

            <Section n={19} title="Changes to Terms">
              <p>
                We may update these Terms from time to time. Continued use of the platform
                means you accept the updated Terms.
              </p>
            </Section>

            <Section n={20} title="Governing Law">
              <p>These Terms shall be governed by the laws of the United Kingdom.</p>
            </Section>

            <Section n={21} title="Contact">
              <p>
                For any questions:{" "}
                <a
                  href="mailto:thrive@loudar.co.uk"
                  className="text-loudar-orange hover:underline"
                  data-testid="terms-contact-email"
                >
                  thrive@loudar.co.uk
                </a>
              </p>
            </Section>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default TermsPage;
